import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  Search, Download, ChevronRight, CalendarDays,
  Filter, Users, X, Clock, Activity, Stethoscope,
  HeartPulse, CheckCircle2, FileText,
} from 'lucide-react';

/* ═══ MOCK DATA ═══ */
const historicalPatients = [
  { id: 1, name: 'Verdi Anna', fiscalCode: 'VRDNNA44S49H501B', startDate: '15/01/2025', endDate: '12/07/2025', setting: 'Media Complessita', status: 'Chiuso', provider: 'Coop. Salute Roma' },
  { id: 2, name: 'Ferrara Elena', fiscalCode: 'FRRLNE53H54H501Z', startDate: '03/06/2025', endDate: null, setting: 'Alta Complessita', status: 'Attivo', provider: 'Consorzio ADI Lazio' },
  { id: 3, name: 'Bianchi Giuseppe', fiscalCode: 'BNCGPP48A15H501R', startDate: '20/09/2024', endDate: '05/03/2025', setting: 'Bassa Complessita', status: 'Chiuso', provider: 'Coop. Riabilita Roma' },
  { id: 4, name: 'Marchetti Teresa', fiscalCode: 'MRCTRS60D45H501P', startDate: '11/11/2025', endDate: null, setting: 'Bassa Complessita', status: 'Attivo', provider: 'ASL Roma 2 - Interno' },
  { id: 5, name: 'Esposito Luca', fiscalCode: 'SPSLCU61M17H501D', startDate: '02/04/2025', endDate: null, setting: 'Alta Complessita', status: 'Sospeso', provider: 'Fondazione Gemelli@Home' },
  { id: 6, name: 'De Luca Concetta', fiscalCode: 'DLCCNT35B52H501F', startDate: '08/01/2024', endDate: '22/06/2024', setting: 'Cure Palliative', status: 'Deceduto', provider: 'Hospice Villa Speranza' },
  { id: 7, name: 'Ricci Marco', fiscalCode: 'RCCMRC57C22H501A', startDate: '05/02/2026', endDate: null, setting: 'Media Complessita', status: 'Attivo', provider: 'Coop. Riabilita Roma' },
];

const STATUS_CONFIG = {
  Attivo:   { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
  Chiuso:   { bg: 'bg-slate-100 dark:bg-slate-700/40', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400' },
  Sospeso:  { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  Deceduto: { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
};

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Tutti i periodi' },
  { value: '6m', label: 'Ultimi 6 mesi' },
  { value: '1y', label: 'Ultimo anno' },
];

function parseDate(str) {
  if (!str) return null;
  const [d, m, y] = str.split('/');
  return new Date(+y, +m - 1, +d);
}

/* ═══ MAIN COMPONENT ═══ */
export default function CronologiaPazienti() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selectedPai, setSelectedPai] = useState(null);

  const filtered = useMemo(() => {
    const now = new Date();
    return historicalPatients.filter(p => {
      /* search */
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.fiscalCode.toLowerCase().includes(q)) return false;
      }
      /* status */
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      /* period */
      if (periodFilter !== 'all') {
        const start = parseDate(p.startDate);
        const months = periodFilter === '6m' ? 6 : 12;
        const cutoff = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
        if (start && start < cutoff) return false;
      }
      return true;
    });
  }, [searchTerm, statusFilter, periodFilter]);

  const handleExportCSV = () => {
    const header = 'Paziente,Codice Fiscale,Data Inizio,Data Fine,Setting,Stato,Erogatore\n';
    const rows = filtered.map(p =>
      `"${p.name}","${p.fiscalCode}","${p.startDate}","${p.endDate || 'In corso'}","${p.setting}","${p.status}","${p.provider}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cronologia_pazienti.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout role="case-manager">
      <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto" data-testid="cronologia-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="cronologia-title">Archivio e Cronologia Pazienti</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Consultazione storica dei percorsi ADI attivi e conclusi</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Users className="w-4 h-4" />
            <span><strong className="text-foreground">{filtered.length}</strong> di {historicalPatients.length} percorsi</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm" data-testid="cronologia-toolbar">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Cerca per nome o codice fiscale..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/50 transition-colors"
              data-testid="search-input"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400 shrink-0" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
              data-testid="status-filter"
            >
              <option value="all">Tutti gli stati</option>
              <option value="Attivo">Attivi</option>
              <option value="Chiuso">Chiusi</option>
              <option value="Sospeso">Sospesi</option>
              <option value="Deceduto">Deceduti</option>
            </select>
          </div>

          {/* Period Filter */}
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
            <select
              value={periodFilter}
              onChange={e => setPeriodFilter(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
              data-testid="period-filter"
            >
              {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Export CSV */}
          <button
            onClick={handleExportCSV}
            className="h-10 px-4 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shrink-0"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> Esporta CSV
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm" data-testid="cronologia-table-container">
          <table className="w-full text-left" data-testid="cronologia-table">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                {['Paziente', 'Periodo Presa in Carico', 'Setting', 'Erogatore', 'Stato', 'Azioni'].map(col => (
                  <th key={col} className="px-5 py-3.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400 dark:text-slate-500">Nessun risultato trovato per i filtri selezionati.</td>
                </tr>
              ) : filtered.map(p => {
                const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.Chiuso;
                return (
                  <tr key={p.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`patient-row-${p.id}`}>
                    {/* Paziente */}
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{p.name}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">{p.fiscalCode}</p>
                    </td>
                    {/* Periodo */}
                    <td className="px-5 py-4">
                      <p className="text-xs text-slate-700 dark:text-slate-200">Dal {p.startDate}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {p.endDate ? `Al ${p.endDate}` : <span className="text-cyan-600 dark:text-cyan-400 font-medium">In corso</span>}
                      </p>
                    </td>
                    {/* Setting */}
                    <td className="px-5 py-4">
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{p.setting}</span>
                    </td>
                    {/* Erogatore */}
                    <td className="px-5 py-4">
                      <span className="text-xs text-slate-600 dark:text-slate-300">{p.provider}</span>
                    </td>
                    {/* Stato */}
                    <td className="px-5 py-4">
                      <Badge className={`${sc.bg} ${sc.text} border-0 text-[10px] font-semibold gap-1.5 px-2.5 py-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {p.status}
                      </Badge>
                    </td>
                    {/* Azioni */}
                    <td className="px-5 py-4">
                      <button onClick={() => setSelectedPai(p)} className="flex items-center gap-1.5 text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors" data-testid={`detail-btn-${p.id}`}>
                        Dettagli Storico <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        <p className="text-[11px] text-slate-400 dark:text-slate-500 text-right">
          Visualizzati {filtered.length} percorsi su {historicalPatients.length} totali
        </p>
      </div>

      {/* ═══ DRAWER via Portal ═══ */}
      {selectedPai && <DrawerStoricoPai pai={selectedPai} onClose={() => setSelectedPai(null)} />}
    </AppLayout>
  );
}

/* ═══ DRAWER COMPONENT ═══ */
function DrawerStoricoPai({ pai, onClose }) {
  const sc = STATUS_CONFIG[pai.status] || STATUS_CONFIG.Chiuso;
  const MOTIVO = { Chiuso: 'Raggiungimento obiettivi clinici', Deceduto: 'Decesso del paziente', Sospeso: 'Ricovero ospedaliero temporaneo' };

  function offsetDate(base, days) {
    const d = parseDate(base);
    if (!d) return '-';
    d.setDate(d.getDate() + days);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  const timelineNodes = [
    { label: 'Presa in carico', date: pai.startDate, icon: FileText, color: 'bg-cyan-500' },
    { label: 'Valutazione UVMD', date: offsetDate(pai.startDate, 14), icon: Stethoscope, color: 'bg-blue-500' },
    { label: 'Avvio Prestazioni', date: offsetDate(pai.startDate, 21), icon: HeartPulse, color: 'bg-emerald-500' },
    ...(pai.endDate ? [{ label: pai.status === 'Deceduto' ? 'Chiusura PAI (Decesso)' : 'Chiusura PAI', date: pai.endDate, icon: CheckCircle2, color: 'bg-slate-400' }] : []),
  ];

  return (
    <>
      <motion.div
        key="drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[60] backdrop-blur-sm"
        onClick={onClose}
        data-testid="drawer-overlay"
      />
      <motion.div
        key="drawer-panel"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl z-[70] flex flex-col"
        data-testid="drawer-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-slate-500 dark:text-slate-300">{pai.name.split(' ').map(w => w[0]).join('')}</span>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate" data-testid="drawer-patient-name">{pai.name}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{pai.fiscalCode}</p>
            </div>
            <Badge className={`${sc.bg} ${sc.text} border-0 text-[10px] font-semibold gap-1.5 px-2.5 py-1 ml-2 shrink-0`} data-testid="drawer-status-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
              {pai.status}
            </Badge>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0" data-testid="drawer-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Recap Percorso */}
          <section data-testid="drawer-section-recap">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Recap Percorso</h3>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Data Inizio', value: pai.startDate },
                  { label: 'Data Fine', value: pai.endDate || <span className="text-cyan-600 dark:text-cyan-400">In corso</span> },
                  { label: 'Setting Assistenziale', value: pai.setting },
                  { label: 'Erogatore', value: pai.provider },
                ].map((f, i) => (
                  <div key={i}>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-medium">{f.label}</p>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mt-0.5">{f.value}</p>
                  </div>
                ))}
              </div>
              {MOTIVO[pai.status] && (
                <div className="pt-3 border-t border-slate-200 dark:border-slate-700/50">
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-medium">Motivo {pai.status === 'Sospeso' ? 'Sospensione' : 'Chiusura'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mt-0.5">{MOTIVO[pai.status]}</p>
                </div>
              )}
            </div>
          </section>

          {/* Sintesi Interventi */}
          <section data-testid="drawer-section-interventi">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Sintesi Interventi Erogati</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Infermieristica', value: '12', unit: 'accessi erogati', icon: HeartPulse, color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-950/30' },
                { label: 'Fisioterapia', value: '8', unit: 'accessi erogati', icon: Activity, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/30' },
                { label: 'Telemonitoraggio', value: '45', unit: 'giorni attivi', icon: Clock, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
              ].map(stat => (
                <div key={stat.label} className={`${stat.bg} rounded-xl p-4 flex flex-col items-center text-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{stat.unit}</p>
                  <p className="text-[9px] font-semibold text-slate-400 dark:text-slate-500 uppercase mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Timeline */}
          <section data-testid="drawer-section-timeline">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Timeline Percorso</h3>
            <div className="relative pl-8">
              <div className="absolute left-[11px] top-3 bottom-3 w-px bg-slate-200 dark:bg-slate-700" />
              <div>
                {timelineNodes.map((node, i) => {
                  const Icon = node.icon;
                  return (
                    <div key={i} className="relative pb-10 last:pb-0" data-testid={`timeline-node-${i}`}>
                      <div className="flex items-start gap-6">
                        <div className={`absolute -left-8 top-1 w-[22px] h-[22px] rounded-full ${node.color} flex items-center justify-center ring-4 ring-white dark:ring-slate-900 shrink-0`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex flex-col gap-1 pt-1">
                          <h4 className="font-semibold text-slate-800 dark:text-white text-base">{node.label}</h4>
                          <span className="text-sm text-slate-500">{node.date}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-900/50 flex justify-end shrink-0" data-testid="drawer-footer">
          <button className="flex items-center gap-2 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid="drawer-download-btn">
            <Download className="w-4 h-4" /> Scarica Cartella Clinica (PDF)
          </button>
        </div>
      </motion.div>
    </>
  );
}
